// src/services/liturgicalService.ts
import api from '../services/api';
import { DailyEnrichment, LiturgicalPost, CreateEnrichmentPayload, CreatePostPayload, UploadMediaPayload } from '../types/liturgical';

// ===== RÉCUPÉRATION DE DONNÉES =====

export const getEnrichmentsByDate = async (month: number, day: number): Promise<DailyEnrichment[]> => {
    const response = await api.get(`/daily-enrichments/by-date/`, {
        params: { month, day }
    });
    return response.data;
};

export const getPostsByDate = async (month: number, day: number): Promise<LiturgicalPost[]> => {
    const response = await api.get(`/liturgical-posts/by-date/`, {
        params: { month, day }
    });
    return response.data;
};

// ===== CRÉATION D'ENRICHISSEMENTS (ADMIN) =====

export const createEnrichment = async (data: CreateEnrichmentPayload) => {
    console.log('📝 [LiturgicalService] Création enrichissement:', data);
    const response = await api.post('/daily-enrichments/', data);
    console.log('✅ [LiturgicalService] Enrichissement créé:', response.data);
    return response;
};

export const updateEnrichment = async (enrichmentId: number, data: Partial<CreateEnrichmentPayload>) => {
    console.log('📝 [LiturgicalService] Mise à jour enrichissement:', enrichmentId, data);
    const response = await api.put(`/daily-enrichments/${enrichmentId}`, data);
    console.log('✅ [LiturgicalService] Enrichissement mis à jour:', response.data);
    return response;
};

// ===== CRÉATION DE POSTS (UTILISATEUR) =====

export const createPost = async (data: CreatePostPayload) => {
    console.log('📝 [LiturgicalService] Création post:', data);
    const response = await api.post('/liturgical-posts/', data);
    console.log('✅ [LiturgicalService] Post créé:', response.data);
    return response;
};

// ===== UPLOAD DE MÉDIAS =====

export const uploadMedia = async (file: File, metadata: UploadMediaPayload) => {
    console.log('📤 [LiturgicalService] Upload média:', { fileName: file.name, ...metadata });

    const formData = new FormData();
    formData.append('file', file);

    // Ajouter les métadonnées
    if (metadata.enrichment_id) {
        formData.append('enrichment_id', metadata.enrichment_id.toString());
    }
    if (metadata.post_id) {
        formData.append('post_id', metadata.post_id.toString());
    }
    if (metadata.caption) {
        formData.append('caption', metadata.caption);
    }
    if (metadata.alt_text) {
        formData.append('alt_text', metadata.alt_text);
    }
    if (metadata.display_order !== undefined) {
        formData.append('display_order', metadata.display_order.toString());
    }

    const response = await api.post('/liturgical/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minutes pour les gros fichiers
    });

    console.log('✅ [LiturgicalService] Média uploadé:', response.data);
    return response;
};

// ===== MODÉRATION DE POSTS (ADMIN) =====

export interface ModeratePostPayload {
    moderation_status: 'approved' | 'rejected' | 'pending';
    moderation_note?: string;
}

export const moderatePost = async (postId: number, payload: ModeratePostPayload) => {
    console.log('[LiturgicalService] Moderating post:', postId, payload);
    const response = await api.post(`/liturgical-posts/${postId}/moderate`, payload);
    console.log('[LiturgicalService] Post moderated:', response.data);
    return response;
};

export const togglePostFeatured = async (postId: number) => {
    console.log('[LiturgicalService] Toggling post featured:', postId);
    const response = await api.post(`/liturgical-posts/${postId}/toggle-featured`);
    console.log('[LiturgicalService] Post featured toggled:', response.data);
    return response;
};

export const deletePost = async (postId: number) => {
    console.log('[LiturgicalService] Deleting post:', postId);
    await api.delete(`/liturgical-posts/${postId}`);
    console.log('[LiturgicalService] Post deleted');
};

// ===== MODÉRATION D'ENRICHISSEMENTS (ADMIN) =====

export const deleteEnrichment = async (enrichmentId: number) => {
    console.log('[LiturgicalService] Deleting enrichment:', enrichmentId);
    await api.delete(`/daily-enrichments/${enrichmentId}`);
    console.log('[LiturgicalService] Enrichment deleted');
};

export const toggleEnrichmentStatus = async (enrichmentId: number) => {
    console.log('[LiturgicalService] Toggling enrichment status:', enrichmentId);
    const response = await api.post(`/daily-enrichments/${enrichmentId}/toggle-status`);
    console.log('[LiturgicalService] Enrichment status toggled:', response.data);
    return response;
};

export default {
    getEnrichmentsByDate,
    getPostsByDate,
    createEnrichment,
    updateEnrichment,
    createPost,
    uploadMedia,
    moderatePost,
    togglePostFeatured,
    deletePost,
    deleteEnrichment,
    toggleEnrichmentStatus,
};
